import { z } from "zod";
import { Environment } from "@/lib/api/schemas/environment";
import { Auth } from "../auth";
import type { ActionReturnType } from "../types";
import { request, validateInput } from "../request";

/**
 * An API project's run targets. The create opts into local error handling: the dialog shows
 * the backend's own message inline (a taken name is a 409 whose `message` says so), so a
 * global toast on top would say it twice.
 */
class EnvironmentsEndpoint {
  public static readonly basePath = "environments";
  public static readonly schemas = {
    list: {
      response: z.object({ count: z.number(), items: Environment.array() }),
    },
    create: {
      body: z.object({
        name: z.string().min(1).max(1024),
        url: z.string().max(2048),
        comment: z.string(),
      }),
      response: Environment,
    },
  };

  constructor(private api: Auth) {}

  public list(query: {
    project_id: number | string;
    active?: boolean;
    /** The backend's default page is 100 — well past what a project keeps, so one page is all of them. */
    limit?: number;
  }): ActionReturnType<z.infer<typeof EnvironmentsEndpoint.schemas.list.response>> {
    return request({
      log: ["Environments", "list"],
      method: "get",
      path: [EnvironmentsEndpoint.basePath],
      query,
      response: EnvironmentsEndpoint.schemas.list.response,
    });
  }

  /** Any environment by id, inactive ones included — the list above only offers what a new run may target. */
  public read(id: number | string): ActionReturnType<z.infer<typeof Environment>> {
    return request({
      log: ["Environments", "read"],
      method: "get",
      path: [EnvironmentsEndpoint.basePath, id],
      response: Environment,
    });
  }

  public create(
    projectId: number | string,
    body: z.input<typeof EnvironmentsEndpoint.schemas.create.body>,
  ): ActionReturnType<z.infer<typeof EnvironmentsEndpoint.schemas.create.response>> {
    const valid = validateInput(EnvironmentsEndpoint.schemas.create.body, body);
    if (!valid.ok) return Promise.resolve(valid);

    return request({
      log: ["Environments", "create"],
      method: "post",
      path: [EnvironmentsEndpoint.basePath],
      query: { project_id: projectId },
      json: valid.data,
      localErrorHandling: true,
      response: EnvironmentsEndpoint.schemas.create.response,
    });
  }
}

export { EnvironmentsEndpoint };
